import React, { useState, useEffect, useRef } from 'react';
import { GoogleLogin, googleLogout } from '@react-oauth/google';
import Home from '../home/Home';
import Welcome from '../welcome/Welcome';
import Rocket from '../rocket/rocket';
import TestModeLayout from '../testmode/TestModeLayout';
import { TalkingHead } from "../../modules/talkinghead";
import "./login-page.css";
import { jwtDecode } from 'jwt-decode';
import { TEST_USERS } from '../../data/testData';

const LoginPage = () => {
    const [successfulLogin, setSuccessfulLogin] = useState(false);
    const [onboardingDone, setOnboardingDone] = useState(null); // null=checking, false=new user, true=done
    const [avatarLoadingProgress, setAvatarLoadingProgress] = useState(0);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const [profile, setProfile] = useState(null);
    const [avatarPresent, setAvatarPresent] = useState(false);
    const [talkingHead, setTalkingHead] = useState();
    const [portalEntered, setPortalEntered] = useState(false);
    const [portalClosing, setPortalClosing] = useState(false);
    const [userId, setUserId] = useState(null);
    const [creditsLeft, setCreditsLeft] = useState(null); // null = unknown

    const headRef = useRef(null);
    const avatarReadyRef = useRef(false);
    const portalEnteredRef = useRef(false);

    const checkUserProfile = async (uid, token) => {
        try {
            const res = await fetch(
                `https://plato-vm.westus.cloudapp.azure.com:5001/Transcript/GetUserProfile?userId=${uid}`,
                { headers: { "Authorization": `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await res.json();
                setCreditsLeft(data.creditsLeft ?? 5);
                setOnboardingDone(true);
            } else if (res.status === 404) {
                setOnboardingDone(false); // new user, show onboarding
            }
        } catch {}
    };

    const handleLoginSuccess = (response) => {
        if (response.credential) {
            localStorage.setItem("google_token", response.credential);
            setSuccessfulLogin(true);
            const decodedUser = jwtDecode(response.credential);
            setProfile({
                name: decodedUser.name,
                email: decodedUser.email,
                picture: decodedUser.picture
            });
            const uid = decodedUser.email;
            setUserId(uid);
            if (TEST_USERS[uid]) {
              setOnboardingDone(true);
            } else {
              checkUserProfile(uid, response.credential);
            }
        }
    };

    const logOut = () => {
        googleLogout();
        setProfile(null);
        setSuccessfulLogin(false);
        setOnboardingDone(null);
        setCreditsLeft(null);
        localStorage.removeItem("google_token");
    };

    useEffect(() => {
        // Auto-login if a valid token is already stored
        const stored = localStorage.getItem("google_token");
        if (stored) {
            try {
                const decoded = jwtDecode(stored);
                if (decoded.exp && decoded.exp > Math.floor(Date.now() / 1000)) {
                    handleLoginSuccess({ credential: stored });
                    return;
                }
            } catch {}
            localStorage.removeItem("google_token");
        }

        // No valid token — preload the login-page avatar
        const preloadAvatar = async () => {
            setAvatarLoading(true);
            setAvatarPresent(true);
            const nodeAvatar = document.getElementById('avatar');
            if (!nodeAvatar.hasChildNodes()) {
                const childDiv = document.createElement('div');
                childDiv.id = "avatarInternal";
                nodeAvatar.appendChild(childDiv);
            }
            const head = new TalkingHead(nodeAvatar, {
                ttsEndpoint: "https://texttospeech.googleapis.com/v1beta1/text:synthesize",
                ttsApikey: "",
                lipsyncModules: ["en"],
                cameraView: "upper"
            });
            headRef.current = head;
            setTalkingHead(head);

            try {
                await head.showAvatar({
                    url: 'avaturn.glb',
                    body: 'F',
                    avatarMood: 'happy',
                    ttsLang: "en-GB",
                    ttsVoice: "en-GB-Standard-A",
                    lipsyncLang: 'en'
                }, (ev) => {
                    if (ev.lengthComputable) {
                        setAvatarLoadingProgress(Math.min(100, Math.round(ev.loaded / ev.total * 100)));
                    }
                });
                setAvatarLoading(false);
                avatarReadyRef.current = true;
                if (portalEnteredRef.current) {
                    await head.speakText("Welcome to Plato. Really nice to meet you. Log in to start learning.");
                }
            } catch (error) {
                setAvatarPresent(true);
                console.log(error);
            }
        };

        const nodeInternal = document.getElementById('avatarInternal');
        if (!avatarPresent && !nodeInternal) preloadAvatar();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (successfulLogin && talkingHead) {
            talkingHead.stopSpeaking();
        }
    }, [successfulLogin, talkingHead]);

    const handlePortalClick = () => {
        if (portalClosing) return;
        if (headRef.current?.audioCtx) {
            headRef.current.audioCtx.resume().catch(() => {});
        }
        setPortalClosing(true);
        setTimeout(() => {
            portalEnteredRef.current = true;
            setPortalEntered(true);
            if (avatarReadyRef.current && headRef.current) {
                headRef.current.speakText("Welcome to Plato. Really nice to meet you. Log in to start learning.");
            }
        }, 900);
    };

    return (
        <div>
            {!successfulLogin ? (
                <>
                    {!portalEntered && (
                        <div
                            className={`portal-overlay${portalClosing ? ' portal-closing' : ''}`}
                            onClick={handlePortalClick}
                        >
                            <div className="portal-burst" />
                            <div className="portal-ring portal-ring-3" />
                            <div className="portal-ring portal-ring-2" />
                            <div className="portal-ring portal-ring-1" />
                            <button className="portal-btn" tabIndex={-1}>
                                <span className="portal-btn-icon">✦</span>
                                <span className="portal-btn-text">ENTER</span>
                            </button>
                            <p className="portal-hint">CLICK TO ENTER THE KNOWLEDGE CENTRE</p>
                        </div>
                    )}

                    <div className="containerLogin" style={{ flexDirection: "row", alignItems: "stretch", justifyContent: "center", overflow: "hidden" }}>
                        <Rocket />

                        <div style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            justifyContent: "center", flex: "0 0 auto", width: "360px",
                            padding: "0 32px", textAlign: "center",
                        }}>
                            <h1 style={{ color: "white", fontSize: "42px", fontWeight: 700, letterSpacing: "8px", margin: "0 0 6px" }}>PLATO</h1>
                            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", letterSpacing: "3px", fontWeight: 500, textTransform: "uppercase", margin: "0 0 28px" }}>YOUR ONLY VIRTUAL TUTOR</p>

                            <div style={{
                                display: "flex", flexDirection: "column", alignItems: "center", gap: "20px",
                                padding: "28px 36px", borderRadius: "20px",
                                background: "rgba(255,255,255,0.07)", backdropFilter: "blur(12px)",
                                WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.12)",
                                boxShadow: "0 16px 40px rgba(0,0,0,0.3)", marginBottom: "20px",
                                width: "100%", boxSizing: "border-box",
                            }}>
                                <p style={{ margin: 0, color: "rgba(255,255,255,0.45)", fontSize: "13px", letterSpacing: "0.5px" }}>
                                    Sign in to start learning
                                </p>
                                <GoogleLogin onSuccess={handleLoginSuccess} onError={() => console.log('Login Failed')} />
                            </div>

                            <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "10px", letterSpacing: "2.5px", fontWeight: 500, textTransform: "uppercase", margin: 0 }}>
                                INSTANT &bull; INTERACTIVE &bull; IMMERSIVE &bull; PERSONALIZED
                            </p>
                        </div>

                        <div style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            justifyContent: "flex-end", flex: "0 0 auto", width: "500px",
                            height: "100vh", overflow: "hidden",
                        }}>
                            {avatarLoading && (
                                <div className="loading-container" style={{ marginBottom: "12px" }}>
                                    <div className="spinner"></div>
                                    <p className="progress-text">AVATAR {avatarLoadingProgress}% LOADED</p>
                                </div>
                            )}
                            <div id="avatar" style={{ width: "500px", height: "600px", flexShrink: 0 }}></div>
                        </div>
                    </div>
                </>
            ) : onboardingDone === false ? (
                <Welcome
                    profile={profile}
                    logOut={logOut}
                    userId={userId}
                    onComplete={() => {
                        setOnboardingDone(true);
                        setCreditsLeft(5);
                    }}
                />
            ) : onboardingDone === true ? (
                TEST_USERS[userId] ? (
                    <TestModeLayout
                        profile={profile}
                        logOut={logOut}
                        userId={userId}
                        creditsLeft={creditsLeft}
                        onCreditUsed={() => setCreditsLeft(c => Math.max(0, (c ?? 1) - 1))}
                    />
                ) : (
                    <Home
                        profile={profile}
                        logOut={logOut}
                        userId={userId}
                        creditsLeft={creditsLeft}
                        onCreditUsed={() => setCreditsLeft(c => Math.max(0, (c ?? 1) - 1))}
                    />
                )
            ) : null /* still checking profile */}
        </div>
    );
};

export default LoginPage;
